<template>
  <button
    :class="[
      'button',
      `variant-${variant}`,
      `size-${size}`,
      { 'block': block },
      { 'disabled': disabled },
      { 'loading': loading },
    ]"
    type="button"
    :disabled="disabled || loading"
    v-bind="$attrs"
    v-on="$listeners"
  >

  <Loader
    v-if="loading"
    size="small"
    class="loader"
    :color="loaderColor"
  />
  <span
    :class="[
      'button-content-container',
      loading && 'isLoading',
    ]">
    <img
      v-if="image.src"
      :src="image.src"
      height="24"
      width="24"
    >
    <slot/>
  </span>

  </button>
</template>

<script>
import Loader, { COLORS } from '@/components/Loader.vue';

const VARIANTS = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  TRANSPARENT: 'transparent',
  BRAND: 'brand',
  GRAY: 'gray',
  BLUE: 'blue',
  PINK: 'pink',
  PURPLE: 'purple',
  ORANGE: 'orange',
  DANGER: 'danger',
};

const SIZES = {
  DEFAULT: 'default',
  SMALL: 'small',
  LARGE: 'large',
};

export default {
  name: 'TButton',
  props: {
    image: {
      type: Object,
      default: () => ({ src: null }),
    },
    variant: {
      type: String,
      default: VARIANTS.PRIMARY,
      validator(value) {
        return Object.values(VARIANTS).includes(value);
      },
    },
    size: {
      type: String,
      default: SIZES.DEFAULT,
      validator(value) {
        return Object.values(SIZES).includes(value);
      },
    },
    block: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Loader,
  },
  computed: {
    loaderColor() {
      let color;

      switch (this.variant) {
        case VARIANTS.PRIMARY:
        case VARIANTS.BLUE:
        case VARIANTS.PURPLE:
        case VARIANTS.ORANGE:
        case VARIANTS.DANGER:
          color = COLORS.WHITE;
          break;
        default:
          color = COLORS.GRAY;
      }

      return color;
    },
  },
};
</script>


<style lang="scss" scoped>
.button {
  position: relative;

  display: inline;
  height: fit-content;
  border-radius: 100px;
  font-family: var(--font-family);
  font-weight: 400;
  color: var(--gray-1);
  border: 1px solid transparent;
  cursor: pointer;
  gap: 8px;

  transition: all 0.1s ease-in-out;
}

.button-content-container {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.isLoading {
  opacity: 0;
  visibility: hidden;
}

.loader {
  position: absolute;
  top: 0;
  left: 0;
}

.block {
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
}

.size {
  &-default {
    padding: 10px 18px;
    font-size: 15px;
    line-height: 18px;
  }

  &-small {
    padding: 7px 12px;
    font-size: 13px;
    line-height: 16px;
  }

  &-large {
    line-height: 30px;
    min-height: 64px;
    padding: 16px 24px;
  }
}

.variant {
  &-primary {
    color: var(--light);
    background-color: var(--gray-1);
  }

  &-blue {
    color: var(--light);
    background-color: var(--blue);
  }

  &-pink {
    color: var(--gray-1);
    background-color: var(--pink);
  }

  &-secondary {
    color: var(--gray-1);
    background-color: var(--light);
    border-color: var(--gray-1);
  }

  &-transparent {
    $color: #e0e3e7;

    background-color: transparent;
    border-color: $color;

    &:hover:enabled,
    &:focus:enabled {
      background-color: $color;
    }
  }

  &-brand {
    color: var(--gray-6);
    background: var(--color-brand-1) var(--brand-gradient);
    border-radius: 100px;
  }

  &-gray {
    color: var(--gray-1);

    background-color: var(--muted);
    border-color: var(--muted);
  }

  &-purple {
    color: var(--light);
    background-color: var(--purple-primary);
  }

  &-orange {
    color: var(--light);
    background-color: var(--orange);
  }

  &-danger {
    color: var(--light);
    background-color: var(--accent);
  }
}

.loading {
  cursor: not-allowed;
}

.disabled {
  cursor: not-allowed;

  &.variant {
    &-primary {
      background-color: var(--muted);
      border-color: var(--muted);
      color: var(--gray-3);
    }

    &-secondary {
      background-color: var(--muted);
      border-color: var(--muted);
      color: var(--gray-1);
    }

    &-brand,
    &-pink,
    &-danger
     {
      opacity: 0.3;
    }

    &-blue {
      background-color: var(--muted);
      color: var(--gray-1);
    }
  }
}
</style>
